import { ReactNode, useEffect, useRef, useState } from "react"
import { Container } from "../Container"
import classNames from "classnames"
import styles from "./Section.module.css"
import { Video } from "../Video"
import { CSSTransition } from "react-transition-group"

interface CustomProps {
  img?: {
    src: string
    alt: string
  }
  children: ReactNode | ReactNode[]
  altLayout?: boolean
  video?: string
}

type SectionProps = CustomProps & React.HTMLAttributes<HTMLDivElement>

export const Section = ({
  img,
  children,
  altLayout,
  video,
  ...rest
}: SectionProps) => {
  const [isVisible, setIsVisible] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true)
          observer.disconnect()
        }
      },
      { threshold: 0.1 }
    )

    if (ref.current) {
      observer.observe(ref.current)
    }

    return () => observer.disconnect()
  }, [])

  return (
    <section
      ref={ref}
      className={altLayout ? styles.rootAlt : styles.root}
      {...rest}
    >
      <Container>
        <div
          className={
            altLayout ? classNames(styles.grid, styles.gridAlt) : styles.grid
          }
        >
          <div
            className={
              altLayout ? classNames(styles.text, styles.textAlt) : styles.text
            }
          >
            {children}
          </div>
          <div
            className={
              altLayout
                ? classNames(styles.image, styles.imageAlt)
                : styles.image
            }
          >
            {video && <Video src={video} />}
            {img && (
              <CSSTransition
                in={isVisible}
                timeout={300}
                classNames={{
                  enter: styles.fadeEnter,
                  enterActive: styles.fadeEnterActive,
                  exit: styles.fadeExit,
                  exitActive: styles.fadeExitActive,
                }}
                unmountOnExit
              >
                <div className={styles.imgContainer}>
                  <img src={img.src} alt={img.alt} loading='lazy' />
                </div>
              </CSSTransition>
            )}
          </div>
        </div>
      </Container>
    </section>
  )
}
